import { useState, useEffect, useCallback, useMemo } from 'react';
import ServicePAUSES from '../services/ServicePAUSES';

/**
 * @typedef {Object} Pause
 * @property {number} id
 * @property {string} name
 * @property {string} description
 * @property {number} time_alert
 * @property {boolean} active
 * @property {boolean} is_sound_active
 * @property {Object|null} sound
 * @property {boolean} is_notification_active
 * @property {boolean} is_repeat_period_active
 * @property {number} period
 * @property {string|null} name_sound
 * @property {string|null} type_sound
 * @property {boolean} disable
 * @property {Array<{id: number, name: string}>} queues
 */

/**
 * Hook para buscar todas as pausas existentes.
 * É possível escolher entre mostrar ou esconder as pausas desabilitadas.
 * @param {Object} [props={}]
 * @param {boolean} [props.showDisabled=false] - Indica se devem ser mostradas pausas desabilitadas
 * @param {boolean} [props.useUserPermissions=false] - Indica se devem ser buscadas apenas as pausas que o usuário tem permissão
 * @returns {Object} Retorna as pausas e funções relacionadas
 */
const usePauses = (props = {}) => {
  const { showDisabled = false, useUserPermissions = false } = props;

  const [state, setState] = useState({
    /** @type {Pause[]} */
    response: [],
    isLoading: false,
    isError: false,
    error: null
  });

  // Pausas em formato de FlexControllerSelectable
  const options = useMemo(
    () =>
      state.response.map((pause) => ({
        label: pause.name,
        value: pause.id
      })),
    [state.response]
  );

  // Pausas agrupadas por ativas e inativas
  const groupedByStatus = useMemo(() => {
    return state.response.reduce(
      (prev, curr) => {
        prev[curr.active ? 'active' : 'inactive'].push(curr);
        return prev;
      },
      {
        active: [],
        inactive: []
      }
    );
  }, [state.response]);

  const fetch = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      const response = useUserPermissions
        ? await ServicePAUSES.getPausesByUserPermission()
        : await ServicePAUSES.getAllPauses();

      /** @type {Pause[]} */
      const data = response?.data?.data ?? [];
      let filteredData = showDisabled
        ? data
        : data.filter((pause) => !pause.disable);

      setState((prevState) => ({
        ...prevState,
        response: filteredData,
        isLoading: false,
        isError: false,
        error: null
      }));
    } catch (err) {
      setState((prevState) => ({
        ...prevState,
        response: [],
        isLoading: false,
        isError: true,
        error: err
      }));
    }
  }, [showDisabled, useUserPermissions]);

  /**
   * Pausa um agente específico.
   * @param {Object} props
   * @param {string} props.login - O login do agente a ser pausado.
   * @param {number} props.pauseId - O ID da pausa a ser aplicada.
   */
  const pauseAgent = useCallback(async ({ login, pauseId }) => {
    try {
      return await ServicePAUSES.pauseAgent(login, pauseId);
    } catch (error) {
      console.error(
        error?.response?.data?.message ?? 'Falha ao pausar o agente:',
        error
      );
      throw error;
    }
  }, []);

  /**
   * Despausa um agente específico.
   * @param {Object} props
   * @param {string} props.login - O login do agente a ser despausado.
   */
  const unpauseAgent = useCallback(async ({ login }) => {
    try {
      return await ServicePAUSES.unpauseAgent(login);
    } catch (error) {
      console.error('Falha ao despausar o agente:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    ...state,
    options,
    groupedByStatus,
    fetch,
    pauseAgent,
    unpauseAgent
  };
};

export { usePauses };
